import { useMemo } from 'react';
import { useAuth } from './useAuth';
import { useImpersonation } from './useImpersonation';
import { useUserRole } from './useUserRole';
import type { AppRole, DepartmentType } from './useUserRole';

/**
 * Centralizes effective identity logic for both real and impersonated sessions.
 * 
 * Returns the effective user identity considering impersonation:
 * - If impersonating a USER: returns that user's actual ID, role, and department
 * - If impersonating a ROLE: returns admin's ID but the impersonated role/department (UI only)
 * - If not impersonating: returns the authenticated user's real identity
 */
export function useEffectiveIdentity() {
  const { user } = useAuth();
  const { activeSession, isImpersonating, loading: impersonationLoading } = useImpersonation();
  const roleData = useUserRole();
  const { 
    role, 
    department, 
    loading: roleLoading,
    isAdmin,
    isProcurementManager,
    isProviderManager,
    isDepartmentManager,
    isQAManager,
    isMAManager,
    isTechApps,
    isTechInfrastructure,
    isCreativeManager,
    canAccessLab,
    canCreateApps,
    canDeleteApps,
    canInviteUsers,
    canManageRoles,
    canViewAllTasks,
    allowedLabs,
    allowedPages
  } = roleData;

  const effectiveIdentity = useMemo(() => {
    // Wait for both impersonation and role to load
    const loading = impersonationLoading || roleLoading;

    if (loading) {
      return {
        effectiveUserId: null,
        effectiveRole: null,
        effectiveDepartment: null,
        impersonationMode: null as 'user' | 'role' | null,
        isImpersonating: false,
        loading: true,
        role: null,
        department: null,
        isAdmin: false,
        isProcurementManager: false,
        isProviderManager: false,
        isDepartmentManager: false,
        isQAManager: false,
        isMAManager: false,
        isTechApps: false,
        isTechInfrastructure: false,
        isCreativeManager: false,
        canAccessLab,
        canCreateApps: false,
        canDeleteApps: false,
        canInviteUsers: false,
        canManageRoles: false,
        canViewAllTasks: false,
        allowedLabs: [],
        allowedPages: []
      };
    }

    // Not impersonating - return real user identity
    if (!isImpersonating || !activeSession) {
      return {
        effectiveUserId: user?.id || null,
        effectiveRole: role,
        effectiveDepartment: department,
        impersonationMode: null as 'user' | 'role' | null,
        isImpersonating: false,
        loading: false,
        role,
        department,
        isAdmin,
        isProcurementManager,
        isProviderManager,
        isDepartmentManager,
        isQAManager,
        isMAManager,
        isTechApps,
        isTechInfrastructure,
        isCreativeManager,
        canAccessLab,
        canCreateApps,
        canDeleteApps,
        canInviteUsers,
        canManageRoles,
        canViewAllTasks,
        allowedLabs,
        allowedPages
      };
    }

    // Impersonating a specific USER
    // NOTE: useUserRole already fetches the impersonated user's role when activeSession.impersonated_user_id exists
    // So `role` and `department` here are already the impersonated user's values
    if (activeSession.impersonated_user_id) {
      return {
        effectiveUserId: activeSession.impersonated_user_id,
        effectiveRole: role, // This is the impersonated user's role (from useUserRole)
        effectiveDepartment: department, // This is the impersonated user's department
        impersonationMode: 'user' as const,
        isImpersonating: true,
        loading: false,
        role, // Impersonated user's role
        department, // Impersonated user's department
        isAdmin,
        isProcurementManager,
        isProviderManager,
        isDepartmentManager,
        isQAManager,
        isMAManager,
        isTechApps,
        isTechInfrastructure,
        isCreativeManager,
        canAccessLab,
        canCreateApps,
        canDeleteApps,
        canInviteUsers,
        canManageRoles,
        canViewAllTasks,
        allowedLabs,
        allowedPages
      };
    }

    // Impersonating a ROLE (UI-only, backend still uses admin's real ID)
    if (activeSession.impersonated_role) {
      return {
        effectiveUserId: user?.id || null,
        effectiveRole: role,
        effectiveDepartment: department,
        impersonationMode: 'role' as const,
        isImpersonating: true,
        loading: false,
        role,
        department,
        isAdmin,
        isProcurementManager,
        isProviderManager,
        isDepartmentManager,
        isQAManager,
        isMAManager,
        isTechApps,
        isTechInfrastructure,
        isCreativeManager,
        canAccessLab,
        canCreateApps,
        canDeleteApps,
        canInviteUsers,
        canManageRoles,
        canViewAllTasks,
        allowedLabs,
        allowedPages
      };
    }

    // Fallback (shouldn't reach here)
    return {
      effectiveUserId: user?.id || null,
      effectiveRole: role,
      effectiveDepartment: department,
      impersonationMode: null as 'user' | 'role' | null,
      isImpersonating: false,
      loading: false,
      role,
      department,
      isAdmin,
      isProcurementManager,
      isProviderManager,
      isDepartmentManager,
      isQAManager,
      isMAManager,
      isTechApps,
      isTechInfrastructure,
      isCreativeManager,
      canAccessLab,
      canCreateApps,
      canDeleteApps,
      canInviteUsers,
      canManageRoles,
      canViewAllTasks,
      allowedLabs,
      allowedPages
    };
  }, [
    user?.id, 
    role, 
    department, 
    isImpersonating, 
    activeSession?.impersonated_user_id,
    activeSession?.impersonated_role,
    impersonationLoading,
    roleLoading,
    isAdmin,
    isProcurementManager,
    isProviderManager,
    isDepartmentManager,
    isQAManager,
    isMAManager,
    isTechApps,
    isTechInfrastructure,
    isCreativeManager,
    canAccessLab,
    canCreateApps,
    canDeleteApps,
    canInviteUsers,
    canManageRoles,
    canViewAllTasks,
    allowedLabs,
    allowedPages
  ]);

  return effectiveIdentity;
}
